import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type EngagementType = 'call' | 'email' | 'note' | 'meeting' | 'tradeshow' | 'sms' | 'other';

interface Engagement {
  id: number;
  contact_id: number;
  engagement_type: EngagementType;
  occurred_at: string;
  owner_email: string;
  subject: string | null;
  body: string | null;
  raw_payload: Record<string, unknown>;
  is_dismissed: boolean;
  dismissed_at: string | null;
  dismissed_by: string | null;
  created_at: string;
  updated_at: string;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; items: Engagement[] };

const TYPES: EngagementType[] = ['call', 'email', 'note', 'meeting', 'tradeshow', 'sms', 'other'];

const TYPE_CHIP: Record<EngagementType, string> = {
  call: 'bg-blue-50 ring-blue-200 text-blue-800',
  email: 'bg-indigo-50 ring-indigo-200 text-indigo-800',
  note: 'bg-slate-100 ring-slate-300 text-slate-800',
  meeting: 'bg-purple-50 ring-purple-200 text-purple-800',
  tradeshow: 'bg-amber-50 ring-amber-200 text-amber-800',
  sms: 'bg-teal-50 ring-teal-200 text-teal-800',
  other: 'bg-zinc-100 ring-zinc-300 text-zinc-800',
};

function engagementTypeChip(t: EngagementType): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ${TYPE_CHIP[t]}`}
    >
      {typeLabel(t)}
    </span>
  );
}

function typeLabel(t: string): string {
  return t
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

interface FormDraft {
  contact_id: string;
  engagement_type: EngagementType;
  occurred_at: string;
  owner_email: string;
  subject: string;
  body: string;
}

function emptyDraft(prefilledContactId?: string): FormDraft {
  return {
    contact_id: prefilledContactId ?? '',
    engagement_type: 'call',
    occurred_at: toDatetimeLocal(new Date()),
    owner_email: '',
    subject: '',
    body: '',
  };
}

export function ManualEngagementsView(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlContactId = searchParams.get('contact_id') ?? '';
  const urlNew = searchParams.get('new') === '1';

  // Filters
  const [filterContactId, setFilterContactId] = useState<string>(urlContactId);
  const [filterType, setFilterType] = useState<string>('');
  const [filterOwner, setFilterOwner] = useState<string>('');
  const [filterFrom, setFilterFrom] = useState<string>('');
  const [filterTo, setFilterTo] = useState<string>('');
  const [includeDismissed, setIncludeDismissed] = useState<boolean>(false);

  // Form
  const [formOpen, setFormOpen] = useState<boolean>(urlNew);
  const [draft, setDraft] = useState<FormDraft>(() => emptyDraft(urlContactId || undefined));
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // List
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [reloadTick, setReloadTick] = useState<number>(0);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<FormDraft>>({});

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const params = new URLSearchParams();
    if (filterContactId) params.set('contact_id', filterContactId);
    if (filterType) params.set('engagement_type', filterType);
    if (filterOwner) params.set('owner_email', filterOwner);
    if (filterFrom) params.set('from', filterFrom);
    if (filterTo) params.set('to', filterTo);
    if (includeDismissed) params.set('include_dismissed', 'true');
    params.set('limit', '200');
    (async () => {
      try {
        const res = await fetch(`/api/manual-engagements?${params.toString()}`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setState({ kind: 'error', message: body.error ?? `Request failed: ${res.status}` });
          return;
        }
        const json = (await res.json()) as { manual_engagements: Engagement[] };
        setState({ kind: 'ready', items: json.manual_engagements });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterContactId, filterType, filterOwner, filterFrom, filterTo, includeDismissed, reloadTick]);

  const reload = (): void => setReloadTick((t) => t + 1);

  async function submitForm(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    const contactIdNum = Number(draft.contact_id);
    if (!Number.isFinite(contactIdNum) || contactIdNum <= 0) {
      setSubmitError('contact_id must be a positive number');
      return;
    }
    const occurredIso = draft.occurred_at ? new Date(draft.occurred_at).toISOString() : '';
    if (!occurredIso) {
      setSubmitError('occurred_at is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/manual-engagements', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          contact_id: contactIdNum,
          engagement_type: draft.engagement_type,
          occurred_at: occurredIso,
          owner_email: draft.owner_email || undefined,
          subject: draft.subject || undefined,
          body: draft.body || undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setSubmitError(body.error ?? `Request failed: ${res.status}`);
        return;
      }
      setDraft(emptyDraft(filterContactId || undefined));
      setFormOpen(false);
      // Strip ?new=1 from URL but preserve contact_id
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
      reload();
    } catch (err) {
      setSubmitError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function dismiss(id: number): Promise<void> {
    const res = await fetch(`/api/manual-engagements/${id}/dismiss`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok || res.status === 409) reload();
  }
  async function restore(id: number): Promise<void> {
    const res = await fetch(`/api/manual-engagements/${id}/restore`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) reload();
  }

  function startEdit(en: Engagement): void {
    setEditingId(en.id);
    setEditDraft({
      engagement_type: en.engagement_type,
      occurred_at: toDatetimeLocal(new Date(en.occurred_at)),
      owner_email: en.owner_email,
      subject: en.subject ?? '',
      body: en.body ?? '',
    });
  }

  async function saveEdit(id: number): Promise<void> {
    const patch: Record<string, unknown> = {};
    if (editDraft.engagement_type) patch.engagement_type = editDraft.engagement_type;
    if (editDraft.occurred_at) patch.occurred_at = new Date(editDraft.occurred_at).toISOString();
    if (editDraft.owner_email !== undefined) patch.owner_email = editDraft.owner_email;
    if (editDraft.subject !== undefined) patch.subject = editDraft.subject;
    if (editDraft.body !== undefined) patch.body = editDraft.body;
    const res = await fetch(`/api/manual-engagements/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (res.ok) {
      setEditingId(null);
      setEditDraft({});
      reload();
    }
  }

  const sortedItems = useMemo<Engagement[]>(() => {
    if (state.kind !== 'ready') return [];
    return [...state.items].sort(
      (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    );
  }, [state]);

  const metrics = useMemo(() => {
    const items = state.kind === 'ready' ? state.items : [];
    const active = items.filter((item) => !item.is_dismissed);
    const contactScoped = filterContactId.trim().length > 0;
    return {
      active: active.length,
      dismissed: items.length - active.length,
      contactScoped,
      latest: active[0]?.occurred_at ?? null,
    };
  }, [filterContactId, state]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-7xl space-y-4">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            System tools
          </p>
          <h1 className="text-2xl font-semibold text-slate-950">Manual evidence</h1>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Capture evidence only when the normal transcript, call, SMS, email, or form flow
            missed something. These records are searchable evidence, so treat them as a fallback
            truth patch rather than a daily work queue.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((v) => !v)}
          className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
        >
          {formOpen ? 'Close capture' : 'Capture evidence'}
        </button>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Active records</div>
          <div className="mt-1 text-2xl font-semibold text-slate-950">
            {state.kind === 'ready' ? metrics.active : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Dismissed</div>
          <div className="mt-1 text-2xl font-semibold text-slate-950">
            {state.kind === 'ready' ? metrics.dismissed : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Scope</div>
          <div className="mt-2 text-sm font-semibold text-slate-950">
            {metrics.contactScoped ? `Contact #${filterContactId}` : 'All contacts'}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Latest active</div>
          <div className="mt-2 text-sm font-semibold text-slate-950">
            {metrics.latest ? formatRelativeDate(metrics.latest) : 'None'}
          </div>
        </div>
      </section>

      {formOpen && (
        <form
          onSubmit={submitForm}
          className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200"
        >
          <div className="mb-4 border-b border-slate-100 pb-4">
            <h2 className="text-sm font-semibold text-slate-950">Capture missing evidence</h2>
            <p className="mt-1 text-xs text-slate-500">
              Prefer source-system ingestion when available. Use this for verified context that
              would otherwise be absent from customer evidence.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              Contact ID
              <input
                type="number"
                required
                value={draft.contact_id}
                onChange={(e) => setDraft({ ...draft, contact_id: e.target.value })}
                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              Type
              <select
                value={draft.engagement_type}
                onChange={(e) =>
                  setDraft({ ...draft, engagement_type: e.target.value as EngagementType })
                }
                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {typeLabel(t)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              Occurred at
              <input
                type="datetime-local"
                required
                value={draft.occurred_at}
                onChange={(e) => setDraft({ ...draft, occurred_at: e.target.value })}
                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
            <label className="text-xs text-slate-600 flex flex-col gap-1">
              Owner email (optional)
              <input
                type="email"
                value={draft.owner_email}
                onChange={(e) => setDraft({ ...draft, owner_email: e.target.value })}
                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
          </div>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Subject
            <input
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Body
            <textarea
              rows={4}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          {submitError && (
            <div className="rounded-md bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-xs text-rose-700">
              {submitError}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 transition disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save evidence'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(emptyDraft(filterContactId || undefined));
                setSubmitError(null);
              }}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              Reset
            </button>
          </div>
        </form>
      )}

      <details className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
        <summary className="cursor-pointer text-sm font-semibold text-slate-950">
          Filters and maintenance
        </summary>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-6">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Contact ID
            <input
              type="number"
              value={filterContactId}
              onChange={(e) => setFilterContactId(e.target.value)}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Type
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
            >
              <option value="">All</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Owner
            <input
              type="email"
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            From
            <input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            To
            <input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-600 inline-flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              checked={includeDismissed}
              onChange={(e) => setIncludeDismissed(e.target.checked)}
            />
            Show dismissed
          </label>
        </div>
      </details>

      {state.kind === 'loading' && (
        <div className="rounded-lg bg-white p-6 text-sm text-slate-500 ring-1 ring-slate-200">
          Loading manual evidence...
        </div>
      )}
      {state.kind === 'error' && (
        <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 p-4 text-sm text-rose-700">
          Error: {state.message}
        </div>
      )}
      {state.kind === 'ready' && sortedItems.length === 0 && (
        <div className="rounded-lg bg-white p-6 text-sm text-slate-500 ring-1 ring-slate-200">
          No manual evidence records match these filters.
        </div>
      )}
      {state.kind === 'ready' && sortedItems.length > 0 && (
        <section className="overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-950">Evidence records</h2>
            <p className="mt-1 text-xs text-slate-500">
              Dismissed records stay out of default retrieval unless restored or explicitly included.
            </p>
          </div>
          <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Subject</th>
                <th className="text-left px-3 py-2 font-medium">Owner</th>
                <th className="text-left px-3 py-2 font-medium">Contact</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedItems.map((en) => {
                const isEditing = editingId === en.id;
                return (
                  <Fragment key={en.id}>
                    <tr className={en.is_dismissed ? 'opacity-50' : ''}>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                        {formatRelativeDate(en.occurred_at)}
                      </td>
                      <td className="px-3 py-2">{engagementTypeChip(en.engagement_type)}</td>
                      <td className="px-3 py-2 text-slate-900 max-w-xs truncate">
                        {en.subject ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{en.owner_email}</td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/contacts/${en.contact_id}`}
                          className="text-slate-600 hover:text-slate-900 underline text-xs"
                        >
                          #{en.contact_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {!en.is_dismissed && (
                          <>
                            <button
                              type="button"
                              onClick={() => (isEditing ? setEditingId(null) : startEdit(en))}
                              className="text-xs text-slate-500 hover:text-slate-900 underline mr-3"
                            >
                              {isEditing ? 'Cancel' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              onClick={() => dismiss(en.id)}
                              className="text-xs text-rose-600 hover:text-rose-800 underline"
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                        {en.is_dismissed && (
                          <button
                            type="button"
                            onClick={() => restore(en.id)}
                            className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                          >
                            Restore
                          </button>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-slate-50">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                            <label className="text-xs text-slate-600 flex flex-col gap-1">
                              Type
                              <select
                                value={(editDraft.engagement_type as string) ?? en.engagement_type}
                                onChange={(e) =>
                                  setEditDraft({
                                    ...editDraft,
                                    engagement_type: e.target.value as EngagementType,
                                  })
                                }
                                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm"
                              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                                    {typeLabel(t)}
                  </option>
                ))}
                              </select>
                            </label>
                            <label className="text-xs text-slate-600 flex flex-col gap-1">
                              Occurred at
                              <input
                                type="datetime-local"
                                value={editDraft.occurred_at ?? toDatetimeLocal(new Date(en.occurred_at))}
                                onChange={(e) =>
                                  setEditDraft({ ...editDraft, occurred_at: e.target.value })
                                }
                                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm"
                              />
                            </label>
                            <label className="text-xs text-slate-600 flex flex-col gap-1 sm:col-span-2">
                              Subject
                              <input
                                type="text"
                                value={editDraft.subject ?? en.subject ?? ''}
                                onChange={(e) =>
                                  setEditDraft({ ...editDraft, subject: e.target.value })
                                }
                                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm"
                              />
                            </label>
                            <label className="text-xs text-slate-600 flex flex-col gap-1 sm:col-span-4">
                              Body
                              <textarea
                                rows={3}
                                value={editDraft.body ?? en.body ?? ''}
                                onChange={(e) =>
                                  setEditDraft({ ...editDraft, body: e.target.value })
                                }
                                className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1 text-sm"
                              />
                            </label>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(en.id)}
                              className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(null);
                                setEditDraft({});
                              }}
                              className="text-xs text-slate-500 underline hover:text-slate-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </section>
      )}
      </div>
    </div>
  );
}
